"use client";

import { useEffect } from "react";
import HeroSection from "./components/AventuraHero";
import NeighborhoodStats from "@/components/feature/NeighborhoodStats";
import RepairServicesGrid from "@/components/feature/RepairServicesGrid";
import ScopeDisclaimer from "@/components/feature/ScopeDisclaimer";
import NeighborhoodCta from "@/components/feature/NeighborhoodCta";
import QuartierCrossLinks from "@/components/feature/QuartierCrossLinks";
import { injectMetaTags } from "@/lib/seoHelpers";
import FaqSection from "@/components/feature/FaqSection";
import RelatedServicesBlock from "@/components/feature/RelatedServicesBlock";
import NearbyAreasBlock from "@/components/feature/NearbyAreasBlock";
import AboutAreaSection from "@/components/feature/AboutAreaSection";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";

const neighborhood = "Aventura";
const zips = ["33180", "33160"];

export default function AventuraPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const aventuraFaqs = [
    {
      question: t("hood.faq1Q"),
      answer: t("hood.faq1A"),
    },
    {
      question: t("hood.faq2Q"),
      answer: t("hood.faq2A"),
    },
    {
      question: t("hood.faq3Q"),
      answer: t("hood.faq3A"),
    },
    {
      question: t("hood.faq4Q"),
      answer: t("hood.faq4A"),
    },
    {
      question: t("aventura.faq5Q"),
      answer: t("aventura.faq5A"),
    },
  ];

  useEffect(() => {
    document.title = currentLang === "es"
      ? "Reparación de Ventanas Aventura — Frente a la Playa"
      : "Aventura Window Repair | Hardware, Glass, Maintenance";

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = currentLang === "es"
      ? "Reparación de ventanas en Aventura. Servicio en condos y casas frente a la playa con herrajes resistentes a sal. El mismo día en Aventura Miami-Dade."
      : "Aventura window repair. Beachfront condo and home service with salt-resistant hardware. Same-day across Aventura Miami-Dade.";

    const cleanupSeo = injectMetaTags({
      title: currentLang === "es"
        ? "Reparación de Ventanas Aventura — Frente a la Playa"
        : "Aventura Window Repair | Hardware, Glass, Maintenance",
      description: currentLang === "es"
        ? "Reparación de ventanas en Aventura. Servicio en condos y casas frente a la playa con herrajes resistentes a sal. El mismo día en Aventura Miami-Dade."
        : "Aventura window repair. Beachfront condo and home service with salt-resistant hardware. Same-day across Aventura Miami-Dade.",
      url: "https://impact-window-repair-miami.com/impact-windows-aventura/",
      image: "https://impact-window-repair-miami.com/og/og-aventura.jpg",
      imageAlt: currentLang === "es"
        ? "Reparación de ventanas Aventura Miami"
        : "Window repair Aventura Miami",
      breadcrumbs: [
        { name: "Home", item: "https://impact-window-repair-miami.com/" },
        { name: "Service Areas", item: "https://impact-window-repair-miami.com/service-areas/" },
        { name: "Aventura", item: "https://impact-window-repair-miami.com/impact-windows-aventura/" }
      ],
      type: "website",
    });

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: currentLang === "es" ? "Reparación de Ventanas" : "Window Repair",
          inLanguage: currentLang === "es" ? "es-US" : "en-US",
          provider: {
            "@type": "LocalBusiness",
            name: "Windows and Doors Repair LLC",
            telephone: "+17863774452",
            email: "contact@impact-window-repair-miami.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "848 Brickell Ave, PH-5",
              addressLocality: "Miami",
              addressRegion: "FL",
              postalCode: "33131",
              addressCountry: "US",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 25.7617,
              longitude: -80.1918,
            },
            openingHoursSpecification: [{
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              opens: "07:00",
              closes: "21:00",
            }],
            priceRange: "$$",
            paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
            currenciesAccepted: "USD",
            areaServed: [
              { "@type": "City", name: "Miami" },
              { "@type": "AdministrativeArea", name: "Miami-Dade County" },
              "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove",
              "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne",
              "South Miami", "North Miami Beach",
            ],
            knowsLanguage: ["en", "es"],
            knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
            slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          },
          areaServed: { "@type": "City", name: "Aventura, Miami-Dade, FL" },
          serviceType: "Window Repair",
          description: currentLang === "es"
            ? "Reparación de herrajes, vidrios y mantenimiento en Aventura."
            : "Hardware repair, glass replacement, and maintenance in Aventura.",
        },
        {
          "@type": "LocalBusiness",
          inLanguage: currentLang === "es" ? "es-US" : "en-US",
          name: "Windows and Doors Repair LLC",
          telephone: "+17863774452",
          email: "contact@impact-window-repair-miami.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "848 Brickell Ave, PH-5",
            addressLocality: "Miami",
            addressRegion: "FL",
            postalCode: "33131",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 25.7617,
            longitude: -80.1918,
          },
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "07:00",
            closes: "21:00",
          }],
          priceRange: "$$",
          paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
          currenciesAccepted: "USD",
          areaServed: [
            { "@type": "City", name: "Miami" },
            { "@type": "AdministrativeArea", name: "Miami-Dade County" },
            "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove",
            "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne",
            "South Miami", "North Miami Beach",
          ],
          knowsLanguage: ["en", "es"],
          knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
          slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          url: window.location.href,
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "main-schema";
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: currentLang === "es" ? "es-US" : "en-US",
      mainEntity: aventuraFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      const s = document.getElementById("main-schema");
      if (s) document.head.removeChild(s);
      const fs = document.getElementById("faq-schema");
      if (fs) document.head.removeChild(fs);
    };
  }, [currentLang]);

  return (
    <main className="min-h-screen">
      <HeroSection h1={t("aventura.h1")} subhead={t("aventura.subhead")} zips={zips} />
      <AboutAreaSection
        badge={currentLang === "es" ? "REPARACIÓN DE VENTANAS AVENTURA" : "AVENTURA WINDOW REPAIR"}
        imageSrc="https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/bc46b801-316f-496c-b466-1a8d5e60a30f_Window-Repair-Services-Across-Aventura.jpg?v=7c96e6c860a752c0afc7f5c6758f7e2a"
        imageAlt={currentLang === "es"
          ? "Especialista en reparación de ventanas Aventura Miami en condo residencial"
          : "Aventura Miami window hardware repair specialist servicing residential condo"}
        heading={currentLang === "es"
          ? "Servicios de Reparación de Ventanas en Aventura"
          : "Window Repair Services Across Aventura"}
        lead={t("aventura.intro")}
        bullets={
          currentLang === "es"
            ? [
                "Reemplazos de herrajes de acero inoxidable resistentes a sal",
                "Reemplazo de vidrios en marcos existentes de condos y casas",
                "Planes de mantenimiento anual para propiedades frente al agua",
              ]
            : [
                "Salt-resistant stainless hardware replacements",
                "Glass replacement in existing condo and home frames",
                "Annual maintenance plans for waterfront properties",
              ]
        }
        closing={currentLang === "es"
          ? "La exposición costera de Aventura justifica mantenimiento dos veces al año — planes programados disponibles."
          : "Aventura coastal exposure justifies twice-yearly maintenance — scheduled plans available."}
      />
      <NeighborhoodStats />
      <RepairServicesGrid heading={t("hood.commonH2")} />
      <ScopeDisclaimer neighborhood={neighborhood} />
      <FaqSection items={aventuraFaqs} heading={t("hood.commonFaqH2")} />
      <RelatedServicesBlock neighborhood={neighborhood} />
      <NearbyAreasBlock neighborhood={neighborhood} neighbors={[
        { name: "Bay Harbor Islands", slug: "bay-harbor-islands" },
        { name: "North Miami Beach", slug: "north-miami-beach" },
        { name: "Miami Beach", slug: "miami-beach" },
      ]} />
      <NeighborhoodCta neighborhood={neighborhood} heading={t("hood.commonCta")} />
      <QuartierCrossLinks />
    </main>
  );
}